import { useCallback, useEffect, useState } from "react";
import { ClipboardList, Package, PackageCheck, Send, Printer, Boxes, Undo2, RotateCcw, FileStack, X } from "lucide-react";
import api, { fmtDate, fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const STAGES = [
  { key: "new", label: "Orders (unpicked)", icon: Boxes },
  { key: "picking", label: "Picking", icon: ClipboardList },
  { key: "packing", label: "Packing", icon: Package },
  { key: "ready_to_ship", label: "Ready to Ship", icon: PackageCheck },
  { key: "dispatched", label: "Dispatched", icon: Send },
];
const NEXT = { new: "picking", picking: "packing", packing: "ready_to_ship", ready_to_ship: "dispatched" };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function WarehouseOps() {
  const [dash, setDash] = useState(null);
  const [stage, setStage] = useState("new");
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState([]);
  const [manifest, setManifest] = useState(null);
  const [manifests, setManifests] = useState([]);
  const [manifestDetail, setManifestDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/ops/dashboard").then(({ data }) => setDash(data)).catch(() => {});
    api.get("/ops/queue", { params: { stage } }).then(({ data }) => { setQueue(data); setSelected([]); }).catch(() => {});
    api.get("/ops/manifests").then(({ data }) => setManifests(data)).catch(() => {});
  }, [stage]);
  useEffect(() => { load(); }, [load]);

  const move = async (sid, to) => {
    try {
      await api.post(`/ops/${sid}/move`, { to });
      toast.success(`Moved to ${to.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const picklist = async () => {
    if (!selected.length) return toast.error("Select shipments to pick");
    const { data } = await api.post("/ops/picklist", { shipment_ids: selected });
    toast.success(`Pick list created — ${data.count} shipments → picking`);
    load();
  };

  const dispatchAll = async () => {
    try {
      const { data } = await api.post("/ops/dispatch", {});
      setManifest(data);
      toast.success(`Manifest created — ${data.count} packages dispatched`);
      load();
    } catch (e) {
      const problems = e.response?.data?.detail?.problems;
      if (problems) problems.slice(0, 4).forEach((p) => toast.error(p));
      else toast.error(fmtErr(e));
    }
  };

  const openManifest = async (m) => {
    try {
      const { data } = await api.get(`/ops/manifest/${m.manifest_id}`);
      setManifestDetail(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="warehouse-ops-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Warehouse Operations</h1>
          <p className="text-sm text-slate-500">Pick → Pack → Ready → Dispatch, with manifests.</p>
        </div>
        {stage === "new" && <button onClick={picklist} data-testid="ops-picklist-btn" className="rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">Generate Pick List ({selected.length})</button>}
        {stage === "ready_to_ship" && <button onClick={dispatchAll} data-testid="ops-dispatch-btn" className="rounded-xl bg-shipany-cyan px-5 py-2.5 text-sm font-bold text-shipany-navy hover:bg-[#0EB4DE] glow-cyan">Dispatch All & Create Manifest</button>}
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="ops-dashboard-cards">
        {[
          ["Orders", dash?.orders, Boxes], ["Picking", dash?.picking, ClipboardList], ["Packing", dash?.packing, Package],
          ["Ready", dash?.ready_to_ship, PackageCheck], ["Dispatched", dash?.dispatched, Send],
          ["Unassigned", dash?.unassigned, Boxes], ["Returns", dash?.returns, Undo2], ["RTO", dash?.rto, RotateCcw],
        ].map(([label, val, Icon]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`ops-card-${label.toLowerCase()}`}>
            <Icon className="h-4 w-4 text-shipany-blue" />
            <p className="mono mt-2 text-xl font-bold text-shipany-navy">{val ?? "—"}</p>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto" data-testid="ops-stage-tabs">
        {STAGES.map((s) => (
          <button key={s.key} onClick={() => setStage(s.key)} data-testid={`ops-tab-${s.key}`}
            className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${stage === s.key ? "bg-shipany-navy text-white" : "border border-slate-200 bg-white text-slate-500"}`}>
            <s.icon className="h-4 w-4" />{s.label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2.5" data-testid="ops-queue">
        {queue.map((s) => (
          <div key={s.shipment_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5" data-testid={`ops-row-${s.awb}`}>
            {stage === "new" && (
              <input type="checkbox" data-testid={`ops-select-${s.awb}`} checked={selected.includes(s.shipment_id)}
                onChange={(e) => setSelected(e.target.checked ? [...selected, s.shipment_id] : selected.filter((x) => x !== s.shipment_id))}
                className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
            )}
            <p className="mono font-semibold text-shipany-blue">{s.awb}</p>
            <p className="text-sm text-slate-600">{s.destination_city} · {s.weight} kg · {s.payment_mode}</p>
            <p className="mono text-xs text-slate-400">{s.courier_name}</p>
            <p className="ml-auto text-xs text-slate-400">{fmtDate(s.created_at)}</p>
            {NEXT[stage] && (
              <button onClick={() => move(s.shipment_id, NEXT[stage])} data-testid={`ops-move-${s.awb}`}
                className="rounded-lg bg-shipany-blue/10 px-3.5 py-1.5 text-xs font-bold text-shipany-blue hover:bg-shipany-blue hover:text-white">
                → {NEXT[stage].replace(/_/g, " ")}
              </button>
            )}
            <a href={`${API_BASE}/api/shipments/${s.shipment_id}/document?type=packing`} target="_blank" rel="noreferrer"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue" title="Packing slip"><Printer className="h-4 w-4" /></a>
          </div>
        ))}
        {queue.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">Nothing in this stage.</div>}
      </div>

      {manifest && (
        <div className="mt-5 flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="manifest-created">
          <p className="text-sm font-semibold text-emerald-800">Manifest {manifest.manifest_id} — {manifest.count} packages · courier {manifest.courier_code}</p>
          <a href={`${API_BASE}/api/ops/manifest/${manifest.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid="manifest-print-btn"
            className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
            <Printer className="h-4 w-4" /> Print Manifest
          </a>
        </div>
      )}

      <div className="mt-8" data-testid="manifest-history">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-shipany-navy"><FileStack className="h-5 w-5 text-shipany-blue" /> Manifests</h2>
        <div className="mt-3 space-y-2.5">
          {manifests.map((m) => (
            <div key={m.manifest_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5" data-testid={`manifest-row-${m.manifest_id}`}>
              <p className="mono font-semibold text-shipany-navy">{m.manifest_id}</p>
              <p className="text-sm text-slate-600">{m.count} packages · {m.courier_code}</p>
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase text-emerald-600">{m.status}</span>
              <p className="ml-auto text-xs text-slate-400">{fmtDateTime(m.created_at)}</p>
              <button onClick={() => openManifest(m)} data-testid={`manifest-view-${m.manifest_id}`}
                className="rounded-lg bg-shipany-blue/10 px-3.5 py-1.5 text-xs font-bold text-shipany-blue hover:bg-shipany-blue hover:text-white">View AWBs</button>
              <a href={`${API_BASE}/api/ops/manifest/${m.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid={`manifest-print-${m.manifest_id}`}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-shipany-blue" title="Print manifest"><Printer className="h-4 w-4" /></a>
            </div>
          ))}
          {manifests.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No manifests yet. Dispatch ready-to-ship orders to create one.</div>}
        </div>
      </div>

      {manifestDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manifest-detail-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setManifestDetail(null)} />
          <div className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Manifest {manifestDetail.manifest_id}</h2>
              <button onClick={() => setManifestDetail(null)} data-testid="manifest-detail-close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{manifestDetail.count} packages · courier {manifestDetail.courier_code} · created {fmtDateTime(manifestDetail.created_at)} · status {manifestDetail.status}</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[560px] text-sm">
                <thead><tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400"><th className="px-4 py-2.5">AWB</th><th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Destination</th><th className="px-4 py-2.5">Weight</th><th className="px-4 py-2.5">Status</th></tr></thead>
                <tbody>
                  {(manifestDetail.shipments || []).map((s) => (
                    <tr key={s.shipment_id} className="border-t border-slate-100" data-testid={`manifest-awb-${s.awb}`}>
                      <td className="mono px-4 py-2.5 font-semibold text-shipany-blue">{s.awb}</td>
                      <td className="mono px-4 py-2.5 text-xs">{s.order_number || "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{s.destination_city} — {s.delivery_pincode}</td>
                      <td className="mono px-4 py-2.5 text-xs">{s.weight} kg</td>
                      <td className="px-4 py-2.5 text-xs">{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <a href={`${API_BASE}/api/ops/manifest/${manifestDetail.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid="manifest-detail-print"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-shipany-navy py-3 text-sm font-bold text-white hover:bg-shipany-blue">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
